import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent } from './shared/RichTextContent';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

/**
 * PEP – Preferred Employers list.
 *
 * A single richtext token renders a formatted list of the participating
 * employer names / organizations. Using richtext (not a slot group per
 * entry) lets on-site editors add, remove, or regroup employers freely
 * without touching code. The section styles the resulting `<ul>` as a
 * responsive multi-column list so long lists stay readable.
 */
export function PepEmployers() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  return (
    <section ref={sectionRef} className="reveal-fade-up pep-employers">
      <div className="pep-employers-container">
        <div className="pep-employers-header">
          <RichTextContent
            path="pepemployers.heading"
            defaultValue="<p>Preferred Employer Partners</p>"
            as="h2"
            className="pep-employers-heading"
          />
        </div>

        <div className="pep-employers-card">
          <RichTextContent
            path="pepemployers.list"
            defaultValue={
              '<ul>' +
              '<li>Local Hospital &amp; Medical Center</li>' +
              '<li>Regional School District</li>' +
              '<li>City Fire &amp; Police Departments</li>' +
              '<li>Municipal Government Employees</li>' +
              '<li>State University Faculty &amp; Staff</li>' +
              '<li>Federal &amp; Military Personnel</li>' +
              '<li>Community College &amp; Trade Schools</li>' +
              '<li>Nonprofit &amp; Faith-Based Organizations</li>' +
              '</ul>'
            }
            as="div"
            className="pep-employers-list"
          />
        </div>

        <RichTextContent
          path="pepemployers.footnote"
          defaultValue='<p>Verification of employment is required at the time of application. Ask our team for the full list of qualifying employers and current program terms.</p>'
          as="p"
          className="pep-employers-footnote"
        />
      </div>

      <style>{`
        .pep-employers {
          padding: 3.5rem 0 4rem;
          background: #f5f6f7;
        }
        @media (min-width: 768px) {
          .pep-employers { padding: 5rem 0 5.5rem; }
        }

        .pep-employers-container {
          max-width: 64rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) { .pep-employers-container { padding: 0 1.5rem; } }
        @media (min-width: 1024px) { .pep-employers-container { padding: 0 2rem; } }

        .pep-employers-header {
          text-align: center;
          max-width: 42rem;
          margin: 0 auto 2.5rem;
        }

        .pep-employers-heading {
          font-family: var(--vibe-font-heading);
          font-size: 1.75rem;
          font-weight: 700;
          line-height: 1.2;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: var(--vibe-color-text);
          margin: 0;
        }
        @media (min-width: 768px) {
          .pep-employers-heading { font-size: 2.25rem; }
        }

        .pep-employers-card {
          background: #ffffff;
          border: 1px solid #e8e8ea;
          border-radius: var(--vibe-radius-lg, 12px);
          padding: 2rem 1.75rem;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
        }
        @media (min-width: 768px) {
          .pep-employers-card { padding: 2.5rem 2.5rem; }
        }

        /* Multi-column list — cascades from 1 → 2 → 3 columns as the
           card widens. Each item gets a small bullet marker in the
           theme accent color. */
        .pep-employers-list :where(ul) {
          list-style: none;
          margin: 0;
          padding: 0;
          column-count: 1;
          column-gap: 2.5rem;
        }
        @media (min-width: 640px) {
          .pep-employers-list :where(ul) { column-count: 2; }
        }
        @media (min-width: 1024px) {
          .pep-employers-list :where(ul) { column-count: 3; }
        }
        .pep-employers-list :where(ol) {
          margin: 0 0 0 1.25rem;
          padding: 0;
        }
        .pep-employers-list :where(li) {
          position: relative;
          padding: 0.55rem 0 0.55rem 1.5rem;
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          line-height: 1.5;
          color: var(--vibe-color-text);
          break-inside: avoid;
          border-bottom: 1px solid #eef0f2;
        }
        .pep-employers-list :where(li)::before {
          content: '';
          position: absolute;
          left: 0.35rem;
          top: 1rem;
          width: 6px;
          height: 6px;
          border-radius: 999px;
          background: var(--vibe-color-primary);
        }
        .pep-employers-list :where(li):last-child {
          border-bottom: none;
        }
        .pep-employers-list :where(p) {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          line-height: 1.6;
          color: var(--vibe-color-text);
          margin: 0 0 0.5rem;
        }
        .pep-employers-list :where(a) {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }
        .pep-employers-list :where(strong) {
          color: var(--vibe-color-text);
        }

        .pep-employers-footnote {
          text-align: center;
          font-family: var(--vibe-font-body);
          font-size: 0.85rem;
          line-height: 1.65;
          color: var(--vibe-color-text-muted);
          margin: 1.75rem auto 0;
          max-width: 42rem;
          font-style: italic;
        }
      `}</style>
    </section>
  );
}

// ─── Content Schema ──────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'PepEmployers',
  displayName: 'PEP – Preferred Employers List',
  tokens: {
    'pepemployers.heading': {
      label: 'Section Heading',
      type: 'richtext',
      defaultValue: '<p>Preferred Employer Partners</p>',
    },
    'pepemployers.list': {
      label: 'Employer List',
      type: 'richtext',
      description:
        'Use a bulleted list — each entry becomes one employer / organization. The list is styled as a responsive multi-column card, so add as many entries as you need.',
      defaultValue:
        '<ul>' +
        '<li>Local Hospital &amp; Medical Center</li>' +
        '<li>Regional School District</li>' +
        '<li>City Fire &amp; Police Departments</li>' +
        '<li>Municipal Government Employees</li>' +
        '<li>State University Faculty &amp; Staff</li>' +
        '<li>Federal &amp; Military Personnel</li>' +
        '<li>Community College &amp; Trade Schools</li>' +
        '<li>Nonprofit &amp; Faith-Based Organizations</li>' +
        '</ul>',
    },
    'pepemployers.footnote': {
      label: 'Footnote',
      type: 'richtext',
      defaultValue:
        '<p>Verification of employment is required at the time of application. Ask our team for the full list of qualifying employers and current program terms.</p>',
    },
  },
};

(PepEmployers as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PepEmployers;
